import MainLayout from 'components/layout/MainLayout';
import React, { useEffect, useState } from 'react';
import { GetServerSideProps } from 'next/types';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import GenericModal from 'components/GenericModal';
import RoundedButton from 'components/RoundedButton';
import styles from './index.module.css';
import SpinToWinLogo from '../../components/svg/SpinToWinLogo.svg';
import SpinToWinPeg from '../../components/svg/SpinToWinPeg.svg';
import Link from 'next/link';
import TransitionReveal from 'components/TransitionReveal';
import { fetchSpinToWinStatusByToken, fetchStartSpinToWinForToken, SpinToWinSpinResponse } from 'utils/api/spin-to-win';
import { useRouter } from 'next/router';
import LoadingSpinner from 'components/LoadingSpinner';
import { ASSET_PREFIX } from 'utils/constants';
import Lottie from 'lottie-react';
import lottieConfetti from '../../asset/lottie/Confetti-2naH1Qyz3l.json';
import { RoundTripSerialized } from 'utils/TypeUtils';

const SPIN_DURATION_MILLIS = 4000;
const SPIN_ROTATIONS = 10;

interface ISpinTheWheelProps {
  
}

/** The list of prizes as they appear in the image file for the wheel, starting from 12'o'clock at the top. The prize
 * wheel GIF should have the top segment spread across the 12'o'clock, position
 */
const prizeCodes = [
  'GREGGS_5',
  'TOKEN_1',
  'COSTA_5',
  'AMAZON_5',
  'ARGOS_5',
  'RAFFLE_5',
] as const;
type PrizeCodes = (typeof prizeCodes)[number];

const prizeInfo: Record<PrizeCodes, { name: string, indefiniteArticle: string }> = {
  COSTA_5: { name: '£5 Costa gift card', indefiniteArticle: 'a' },
  AMAZON_5: { name: '£5 Amazon gift card', indefiniteArticle: 'a' },
  ARGOS_5: { name: '£5 Argos gift card', indefiniteArticle: 'a' },
  RAFFLE_5: { name: '5 DAYMADE raffle entries', indefiniteArticle: '' },
  GREGGS_5: { name: '£5 Greggs gift card', indefiniteArticle: 'a' },
  TOKEN_1: { name: 'DAYMADE token', indefiniteArticle: 'a' },
};

const SpinTheWheel = (props: ISpinTheWheelProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLinkAlreadyUsed, setIsLinkAlreadyUsed] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);  
  const [prizeCode, setPrizeCode] = useState<PrizeCodes | undefined>(undefined);
  const [prizeAngleOffset, setPrizeAngleOffset] = useState(0);
  const [firstName, setFirstName] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [spinState, setSpinState] = useState<'WAITING' | 'LOADING_SPIN_RESULT' | 'SPINNING' | 'SPUN' | 'PRIZE_VISIBLE'>('WAITING')
  const [resetCount, setResetCount] = useState(2);
  const router = useRouter();
  const testPrizeCode = (Array.isArray(router.query.spintowintest) ? router.query.spintowintest[0] : router.query.spintowintest) || '';
  const token = (Array.isArray(router.query.token) ? router.query.token[0] : router.query.token) || '';


  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = testPrizeCode ? { playerFirstName: 'test', tokenInfo: { awarded_at: ''}, expired: false } : await fetchSpinToWinStatusByToken(token);

        if ("reason" in response) {
          setErrorMessage('Sorry, the link you followed was not valid');
        } else {
          setFirstName(response.playerFirstName);
          if (response.tokenInfo.awarded_at) {
            setIsLinkAlreadyUsed(true);
          } else if (response.expired) {
            setIsLinkExpired(true);
          }
        }
      } catch (e) {
        console.error(e);
        setErrorMessage('Sorry, something went wrong. Please try again later.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const reset = () => {
    setSpinState('WAITING');
    setPrizeCode(undefined);
    setResetCount(x => x + 1);
  };

  const startSpin = async () => {
    (async () => {
      setSpinState('LOADING_SPIN_RESULT');
      const result = testPrizeCode
        ? await new Promise<RoundTripSerialized<SpinToWinSpinResponse>>(resolve => setTimeout(() => resolve({
          tokenInfo: {
            prize_code: prizeInfo[testPrizeCode as PrizeCodes] ? testPrizeCode : prizeCodes[Math.floor(Math.random() * prizeCodes.length)]
          } as any,
          expired: false,
          alreadySpun: false
        }), 500))
        : await fetchStartSpinToWinForToken(token);
      if (!result || result && "reason" in result) {
        setErrorMessage('Sorry, something went wrong.');
        setSpinState('SPUN');
      } else if (result.expired) {
        setIsLinkExpired(true);
        setSpinState('SPUN');
      } else if (result.alreadySpun) {
        setIsLinkAlreadyUsed(true);
        setSpinState('SPUN');
      } else if (prizeCodes.indexOf(result.tokenInfo.prize_code as PrizeCodes) < 0) {
        // Check that the server returned a prize that we know how to handle
        setErrorMessage('Sorry, something went wrong.');
        setSpinState('SPUN');
      } else {
        setSpinState('SPINNING');
        setPrizeCode(result.tokenInfo.prize_code as PrizeCodes);
        setPrizeAngleOffset(Math.floor(Math.random() * 5) - 2);
        setTimeout(onSpinComplete, SPIN_DURATION_MILLIS);
      }
    })();
  };
  const onSpinComplete = () => {
    setSpinState('SPUN');
    setTimeout(() => { setSpinState('PRIZE_VISIBLE'); }, 500);
  }; 

  const prizeAngle = prizeCode != undefined
    ? (360 * (prizeCodes.indexOf(prizeCode) / prizeCodes.length) + 12 * prizeAngleOffset)
    : undefined;
  
  return (
    <MainLayout limitItemWidths hideHeaderOnMobile>
      <div className="aboutUsTypographyReset">
        {loading
          ? <LoadingSpinner loadingText={undefined} />
          : <AboutUsModule background="tealGradient">
            <AboutUsSiteContainer positionRelative>
              <div className={styles.backgroundConfetti}>
                <img src={ASSET_PREFIX + '/images/spin-to-win/BackgroundConfetti.svg'} alt="" />
              </div>
              <div className={styles.backgroundCard}>
              </div>
              <div className={styles.mainContainer}>
                <div className={styles.introTextContainer + ' withReducedSpacing'}>
                  <h1>
                    <span className={styles.mobileOnlyLogo}><img src={ASSET_PREFIX + '/images/daymade-logo.png'} alt="" /></span>
                    <SpinToWinLogo aria-label="The DAYMADE Spin2Win" />
                  </h1>
                  <p>
                    Welcome{firstName ? (', ' + firstName) : ''}!<br />
                    Spin the wheel to win
                  </p>
                </div>
                <div className={styles.wheelContainer}>
                  <div className={styles.wheelOuter}>
                    <div className={styles.wheelInner}>
                      <img
                        key={resetCount}
                        className={styles.wheel + ' ' + (spinState == 'SPINNING' ? styles.spinning : '')}
                        style={prizeAngle !== undefined
                          ? { transform: 'rotate(' + (SPIN_ROTATIONS * 360 - prizeAngle) + 'deg)' }
                          : {}
                        }
                        src={ASSET_PREFIX + '/images/spin-to-win/wheel202510.jpg'}
                        alt="Image of spin to win wheel"
                      />
                    </div>
                    <div key={resetCount * 2} className={styles.wheelPeg + ' ' + (prizeCode && (spinState == 'SPINNING' || spinState == 'SPUN') ? styles.spinning : '')}>
                      <SpinToWinPeg />
                    </div>
                  </div>
                </div>
                <div className={styles.buttonContainer}>
                  <p>
                    {(spinState == 'WAITING' || spinState == 'LOADING_SPIN_RESULT')
                      ? <RoundedButton rebrandDm2025 onClick={startSpin} loading={spinState == 'LOADING_SPIN_RESULT'}>
                        {testPrizeCode ? 'Test' : 'Spin'} the wheel
                      </RoundedButton>
                      : <span className={styles.spinningMessage}><strong>
                        {spinState == 'SPINNING' ? 'Spinning...' : '\xa0'}
                      </strong>
                      </span>
                    }
                  </p>
                  <p>
                    <Link href="/terms-and-conditions"><a>Terms and Conditions</a></Link>
                    {testPrizeCode &&
                      <>
                        <br />
                        <RoundedButton rebrandDm2025 onClick={reset}>
                          Reset
                        </RoundedButton>
                      </>
                    }
                  </p>
                </div>
              </div>
              {spinState == 'PRIZE_VISIBLE' && prizeCode && (
                <TransitionReveal className={styles.prizeSheet} revealClassName={styles.visible}>
                  <div className={styles.prizeSheetBackground}>
                  </div>
                  <div className={styles.prizeSheetInner}>
                    <img className={styles.prizeSheetImage} alt="" src={ASSET_PREFIX + '/images/spin-to-win/' + prizeCode + '.png'} />
                    <h2 className="aboutUsTypographyHeaderXlargeOnWhite" >Congrats!!</h2>
                    <p>
                      {testPrizeCode ? 'Test mode got ' : 'You won yourself '}{prizeInfo[prizeCode].indefiniteArticle}<br />
                      <strong>{prizeInfo[prizeCode].name}</strong> 🥳
                    </p>
                    <p>
                      {!testPrizeCode
                        ? 'Make sure to check your email to claim your reward'
                        : 'Test mode entries won\'t result in an email being sent'
                      }
                    </p>
                    <p>
                      <RoundedButton rebrandDm2025 href="/">Back to DAYMADE</RoundedButton>
                      {testPrizeCode &&
                        <>
                        <br /><br />
                        <RoundedButton rebrandDm2025 onClick={reset}>
                            Reset
                        </RoundedButton>
                        </>
                      }
                    </p>
                  </div>
                </TransitionReveal>
              )}
            </AboutUsSiteContainer>
          </AboutUsModule>
        }
        {(spinState == 'PRIZE_VISIBLE' && prizeCode) &&
          <Lottie animationData={lottieConfetti} loop={false} className={styles.confetti} />
        }
        {(errorMessage || isLinkAlreadyUsed || isLinkExpired) &&
          <GenericModal>
            <p>
              {errorMessage}
              {isLinkAlreadyUsed ? 'This link has already been used'
                : isLinkExpired
                  ? 'This link has expired'
                  : ''}
            </p>
            <p><RoundedButton rebrandDm2025 href="/">Back to DAYMADE</RoundedButton></p>
          </GenericModal>
        }
      </div>
    </MainLayout>
  );
};


export const getServerSideProps: GetServerSideProps = async (ctx) => {

  return {
    props: {
    },
  };
};

export default SpinTheWheel;
